import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { StateContext } from '../../../../GlobalStateStore';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Divider from '@amzn/meridian/divider';
import {
  BottomQuartileCategory,
  BottomQuartileData,
  TrendAndDetailsMetrics,
} from './types';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import styles from './ViewStyle.module.css';
import stylesFilter from '../../../CCXDetails/CCXDetailsPage.module.css';

import Box from '@amzn/meridian/box';
import SearchField from '@amzn/meridian/search-field';
import { BottomQuartileMap } from './types';
import {
  getMetricStringIdAndDefaultMessage,
  getRecentMonths,
} from '../../../Goals/helper';
import {
  BOTTOM_QUARTILE_TABLE_SIZE,
  BottomQuartileTableColumns,
  computeColumnWidthAsPercent,
  computeMetricDifference,
  filterCCXMetricsForRemoval,
  filterCCXNeg,
  findAliasFromIdWithPrefix,
  findFullNameFromIdWithPrefix,
  formatMetric,
  getLookbackForBQ,
  sortBottomQuartile,
} from './helper';
import { AssociateInsight, TeamProfile } from '../../../../Types';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { SortDirection } from '../../../../Types/SortDirection';
import { TableSortDirection } from '@amzn/meridian/table/table';
import DataState from '../../../../Types/DataState';
import Pagination from '@amzn/meridian/pagination';
import { NoDataBadge } from './NoDataBadge';
import { useParams } from 'react-router';
import moment from 'moment';
import {
  PresetDate,
  TimeOption,
  getMonthDateRange,
} from '@amzn/aet-galactus-kit';
import DataSource from '../../../../api';
import useCustomTracking from '../../../../Util/useCustomTracking';
import {
  getBQMetricsList,
  formatMonthMetrics,
} from '../../AssociateRichCards/Util/team';
import Loader from '@amzn/meridian/loader';
import { useLocation } from 'react-router';
import { TIME_TABLE } from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import infoTokens from '@amzn/meridian-tokens/base/icon/info';
import Tooltip from '@amzn/meridian/tooltip';
import Icon from '@amzn/meridian/icon';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';

interface BottomQuartileProps {
  teamProfile: TeamProfile;
  teamProfileState: string;
  vertical: string;
}
const BottomQuartile: React.FC<BottomQuartileProps> = ({
  teamProfile,
  teamProfileState,
  vertical,
}: BottomQuartileProps): React.ReactElement => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const defaultSearch = params.get('bottomUser');
  const defaultMetrics = params.get('bottomMetrics');
  const defaultPercentile = params.get('bottomPercentile');

  const intl = useIntl();
  let { userId } = useParams() as {
    userId: string;
  };

  const trendAndDetailsMetrics = null;

  const apiNaws = DataSource.getDataFeed('NAWS');

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState?.state;
  const { isTm, requesterUserID, requesterUsername, userID, cscEntityPrefix } =
    inspireOptions.data;
  userId = userId || userID;

  const [bqSelection, setBqSelection] = useState<BottomQuartileCategory>(
    defaultPercentile
      ? (defaultPercentile as BottomQuartileCategory)
      : BottomQuartileCategory.TWENTY_FIVE
  );

  const enableCCXRemoval = isFeatureEnabled(Feature.ENABLE_CCX_REMOVAL);

  const selectedMetricFallback = enableCCXRemoval
    ? BottomQuartileMap.RAP
    : isFeatureEnabled(Feature.CCX_BOTTOM_TWO_BOX)
      ? BottomQuartileMap.CCX_NEG
      : BottomQuartileMap.CCX_NEG_ONEBOX;

  const [multiSelectedMetric, setMultiSelectMetric] = useState(() => {
    if (defaultMetrics) {
      const metricsFromUrl = filterCCXMetricsForRemoval(
        defaultMetrics.split(',').map(metric => metric.trim())
      );
      return metricsFromUrl.length > 0
        ? metricsFromUrl
        : [selectedMetricFallback];
    }
    return [selectedMetricFallback];
  });
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>(
    undefined
  );
  const [associateInsights, setAssociateInsights] = useState<
    AssociateInsight[]
  >([]);
  const [associateInsightsState, setAssociateInsightsState] = useState(
    DataState.LOADING
  );
  const [teamDetailsMetrics, setTeamDetailsMetrics] = useState<
    TrendAndDetailsMetrics[]
  >([]);
  const [displayedBottomQuartileData, setDisplayedBottomQuartileData] =
    useState<BottomQuartileData[]>([]);
  const [teamDetailsMetricsState, setTeamDetailsMetricsState] = useState(
    DataState.LOADING
  );
  const [searchValue, setSearchValue] = useState<string>(defaultSearch ?? '');
  const [sortColumn, setSortColumn] = useState(BottomQuartileTableColumns.Name);
  const [tableData, setTableData] = useState<
    Record<string, Record<string, string>>
  >({});
  const [monthList, setMonthList] = useState<
    { year: number; month: string; monthNum: number }[]
  >([]);
  const [sortDirection, setSortDirection] = useState<TableSortDirection>(
    SortDirection.ASCENDING
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const onSort = ({
    sortColumn,
    sortDirection,
  }: {
    sortColumn: BottomQuartileTableColumns;
    sortDirection: TableSortDirection;
  }) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  const firstVisibleIndex = (currentPage - 1) * BOTTOM_QUARTILE_TABLE_SIZE;
  const lastVisibleIndex = firstVisibleIndex + BOTTOM_QUARTILE_TABLE_SIZE;

  const sortData = (
    bottomQuartileData: BottomQuartileData[]
  ): BottomQuartileData[] => {
    return [...bottomQuartileData].sort(
      sortBottomQuartile(sortColumn, sortDirection)
    );
  };

  const isEligibleForDisplaying = (
    metricLabel: string,
    bqSelection: BottomQuartileCategory
  ): boolean => {
    switch (metricLabel) {
      case 'ONE':
        return true;
      case 'TEN':
        return (
          bqSelection === BottomQuartileCategory.TWENTY_FIVE ||
          bqSelection === BottomQuartileCategory.TEN
        );
      case 'TWENTYFIVE':
        return bqSelection === BottomQuartileCategory.TWENTY_FIVE;
      default:
        return false;
    }
  };

  useEffect(() => {
    if (userId && teamProfileState === DataState.LOADED) {
      // getting bottom quartile of the selected month
      const associateInsightQueryVariable =
        apiNaws.prepareQueryForGetAssociateInsights(
          `${cscEntityPrefix}:${userId}`,
          teamProfile.reportingTeam.map(
            reportingTeam =>
              `${cscEntityPrefix}:${reportingTeam.relatedUser.userID}`
          ),
          teamProfile.reportingTeam.map(
            reportingTeam => reportingTeam.relatedUser.alias
          ),
          TimeOption.DAILY,
          moment(selectedMonth, 'YYYY-M')
            .add(1, 'month') // currently somehow backend responds with current month and no need to +1 month
            .clone()
            .format('YYYY-MM-DD')
        );

      apiNaws.getAssociateInsights(
        associateInsightQueryVariable,
        setAssociateInsights,
        setAssociateInsightsState
      );
    }
  }, [userId, teamProfileState, selectedMonth]);

  useEffect(() => {
    if (
      vertical === '' ||
      !vertical ||
      teamProfile.user.userID === '' ||
      teamProfileState === DataState.LOADING
    ) {
      return;
    }

    const { startDateOfMonth, endDateOfMonth } = getMonthDateRange(
      moment(selectedMonth, 'YYYY-M').clone().format('YYYY-MM-DD')
    );
    const customDateMonth = [startDateOfMonth, endDateOfMonth];

    const queryLastMonthTeamDetailsMetrics =
      apiNaws.prepareTeamDetailsMetricsQuery(
        userId,
        cscEntityPrefix,
        teamProfile.reportingTeam,
        vertical,
        'ALL',
        'ALL',
        'ALL',
        'ALL',
        PresetDate.MONTHLY,
        customDateMonth,
        getBQMetricsList()
      );
    apiNaws.getTeamDetailsMetrics(
      queryLastMonthTeamDetailsMetrics,
      setTeamDetailsMetrics,
      setTeamDetailsMetricsState
    );
  }, [teamProfile, vertical, selectedMonth]);

  useEffect(() => {
    if (
      teamDetailsMetricsState !== DataState.LOADED ||
      associateInsightsState !== DataState.LOADED
    )
      return;

    let filteredBottomQMetrics: BottomQuartileData[] = [];
    for (const userData of teamDetailsMetrics) {
      // skip if user is not bottom quartile
      let bottomQUser = associateInsights.find(insight => {
        return userData.userID.includes(
          insight.associate.split('_').slice(-1)[0]
        );
      });
      if (!bottomQUser) continue;

      // push metrics data as user is bottom quartile for the selection set
      for (const metric of multiSelectedMetric) {
        let selectedUserBottomQ =
          bottomQUser.bottomQuartileMetrics.find(
            metrics =>
              metrics.metricName === metric &&
              isEligibleForDisplaying(metrics.label, bqSelection)
          ) ?? undefined;
        if (!selectedUserBottomQ) continue;

        let targetMetric = userData.metricDatas?.trend?.find(
          trendInfo => trendInfo.name === metric
        );

        if (!targetMetric) {
          // no data from getTeamDetails
          filteredBottomQMetrics.push({
            userFullName: findFullNameFromIdWithPrefix(
              userData.userID,
              teamProfile
            ),
            userAlias: findAliasFromIdWithPrefix(userData.userID, teamProfile),
            metricName: undefined,
            metricValue: undefined,
            networkAvg: undefined,
            benchmarkDiff: undefined,
          });
        } else {
          // has data from getTeamDetails
          let formatData = formatMonthMetrics(targetMetric).slice(-1)[0];

          const isTimeMetric =
            formatData.metric in TIME_TABLE
              ? TIME_TABLE[formatData.metric as keyof typeof TIME_TABLE]
              : false;

          filteredBottomQMetrics.push({
            userFullName: findFullNameFromIdWithPrefix(
              userData.userID,
              teamProfile
            ),
            userAlias: findAliasFromIdWithPrefix(userData.userID, teamProfile),
            metricName: formatData.metric,
            metricValue: isTimeMetric
              ? formatData.value
              : (Number(formatData.value) * 100).toString(),
            networkAvg: formatData.netAvg
              ? isTimeMetric
                ? formatData.netAvg
                : (Number(formatData.netAvg) * 100).toString()
              : undefined,
            benchmarkDiff:
              formatData.value && formatData.netAvg
                ? computeMetricDifference(
                    formatData.value,
                    formatData.netAvg,
                    true
                  )
                : undefined,
          });
        }
      }
    }
    // search bar filtering
    let filteredSearchBottomQMetrics: BottomQuartileData[] = [];
    const trimmedSearchValue = searchValue.trim().toLowerCase();
    for (const displayedData of filteredBottomQMetrics) {
      if (
        displayedData.userFullName.toLowerCase().includes(trimmedSearchValue) ||
        displayedData.userAlias.includes(trimmedSearchValue)
      ) {
        filteredSearchBottomQMetrics.push(displayedData);
      }
    }

    // reset pagination & update table content
    setCurrentPage(1);
    setDisplayedBottomQuartileData(sortData(filteredSearchBottomQMetrics));
  }, [
    teamDetailsMetricsState,
    associateInsightsState,
    multiSelectedMetric,
    bqSelection,
    searchValue,
  ]);

  useEffect(() => {
    setCurrentPage(1);
    const sortedData = sortData(displayedBottomQuartileData);
    setDisplayedBottomQuartileData(sortedData);
  }, [sortColumn, sortDirection]);

  useEffect(() => {
    setTotalPages(
      Math.ceil(displayedBottomQuartileData.length / BOTTOM_QUARTILE_TABLE_SIZE)
    );
  }, [displayedBottomQuartileData]);

  useEffect(() => {
    // populate select options
    const pastMonths = getRecentMonths(getLookbackForBQ());
    setMonthList(pastMonths);

    // default select to previous month
    if (pastMonths[0]) {
      setSelectedMonth(
        `${pastMonths[0].year}-${String(pastMonths[0].monthNum)}`
      );
    }
  }, [trendAndDetailsMetrics]);

  const compareByBottomQuartileButtonGroupClickStreamContentId = `metrics-compare-data-by-bottom-quartile-${bqSelection.toLowerCase()}-button`;
  const [
    BottomQuartileSelectionButtonGroupClickStreamTrackingTag,
    emitBottomQuartileSelectionClickstreamTags,
  ] = useCustomTracking(
    compareByBottomQuartileButtonGroupClickStreamContentId,
    'metrics-slot',
    {
      'data-csa-c-type': 'button',
      'data-csa-c-content-id':
        compareByBottomQuartileButtonGroupClickStreamContentId,
      'data-csa-c-tag1': 'Metrics',
      'data-csa-c-tag2': `metrics/${userID}`,
      'data-csa-c-profile-id': requesterUserID,
      'data-csa-c-username': requesterUsername,
      'data-csa-c-is-tm': isTm,
    }
  );

  function changeBottomQuartileSelection(
    newBottomQuartileSelection: BottomQuartileCategory
  ) {
    setBqSelection(newBottomQuartileSelection);
    emitBottomQuartileSelectionClickstreamTags();
  }

  return (
    <>
      <Divider spacingBefore='500' spacingAfter='400' />

      <>
        <Row
          width='100%'
          widths='fill'
          alignmentHorizontal='end'
          alignmentVertical='center'
        >
          <Text type='h500'>
            <FormattedMessage
              id='cs_perfportal_arc_bq_bottom_quartile_associates'
              defaultMessage='Bottom Quartile Associates'
            />
          </Text>

          <Row
            width='100%'
            widths='fill'
            alignmentHorizontal='end'
            alignmentVertical='center'
          >
            <Row
              spacingInset='100'
              minHeight={60}
              alignmentHorizontal='end'
              data-cy='bottom-quartile-category'
            >
              {' '}
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_metrics_compare_data_by'
                  defaultMessage='Compare Data By'
                />
              </Text>
              <BottomQuartileSelectionButtonGroupClickStreamTrackingTag />
              <ButtonGroup
                value={bqSelection}
                onChange={changeBottomQuartileSelection}
              >
                <ButtonOption
                  value={BottomQuartileCategory.TWENTY_FIVE}
                  data-cy='twentyfive'
                >
                  <FormattedMessage
                    id='cs_perfportal_bq_twentyfive'
                    defaultMessage='Bottom 25%'
                  />
                </ButtonOption>

                <ButtonOption value={BottomQuartileCategory.TEN} data-cy='ten'>
                  <FormattedMessage
                    id='cs_perfportal_bq_ten'
                    defaultMessage='Bottom 10%'
                  />
                </ButtonOption>
                <ButtonOption value={BottomQuartileCategory.ONE} data-cy='one'>
                  <FormattedMessage
                    id='cs_perfportal_bq_one'
                    defaultMessage='Bottom 1%'
                  />
                </ButtonOption>
              </ButtonGroup>
            </Row>
          </Row>
        </Row>
        <Column width={676} spacingInset='none none 300 none'>
          <Text type='b400' className={styles.subdued}>
            <FormattedMessage
              id='cs_perfportal_bq_description'
              defaultMessage='Trends shown are for associates in the bottom quartile for the selected month.'
            />
          </Text>
        </Column>
        <>
          <Box spacingInset='400' className={stylesFilter.filtersContainer}>
            <Column alignmentVertical='center'>
              <SearchField
                value={searchValue}
                onChange={setSearchValue}
                placeholder={intl.formatMessage({
                  id: 'cs_perfportal_bq_search_for',
                  defaultMessage: 'Search for...',
                })}
                onSubmit={() => {}}
                searchButton={true}
                width={300}
              />
            </Column>
            <Row
              width='100%'
              widths='fill'
              alignmentHorizontal='end'
              alignmentVertical='center'
            >
              <Row
                spacingInset='100'
                minHeight={60}
                alignmentHorizontal='end'
                data-cy='metric-category'
              >
                <Select
                  label={intl.formatMessage({
                    id: 'cs_perfportal_metrics_metric_type',
                    defaultMessage: 'Metric Type',
                  })}
                  value={multiSelectedMetric}
                  onChange={setMultiSelectMetric}
                  placeholder={intl.formatMessage({
                    id: 'cs_perfportal_goal_enter_value',
                    defaultMessage: 'Enter value...',
                  })}
                  width={288}
                  size='xlarge'
                >
                  {filterCCXMetricsForRemoval(
                    filterCCXNeg(Object.values(BottomQuartileMap))
                  ).map((metricOption: BottomQuartileMap) => {
                    return (
                      <SelectOption
                        value={metricOption}
                        label={intl.formatMessage(
                          getMetricStringIdAndDefaultMessage(metricOption)
                        )}
                        key={metricOption}
                        data-csa-c-type='select'
                        data-csa-c-content-id='metrics-bottom-quartile-metric-select'
                        data-csa-c-slotid='metrics-slot'
                        data-csa-c-tag1='Metrics'
                        data-csa-c-tag2={`metrics/${userID}`}
                        data-csa-c-tag3={`${metricOption}`}
                        data-csa-c-profile-id={requesterUserID}
                        data-csa-c-username={requesterUsername}
                        data-csa-c-is-tm={isTm}
                      />
                    );
                  })}
                </Select>

                <Select
                  label={intl.formatMessage({
                    id: 'cs_perfportal_metrics_view_by_month',
                    defaultMessage: 'View by Month',
                  })}
                  value={selectedMonth}
                  onChange={setSelectedMonth}
                  placeholder={intl.formatMessage({
                    id: 'cs_perfportal_goal_enter_value',
                    defaultMessage: 'Enter value...',
                  })}
                  width={288}
                  size='xlarge'
                >
                  {monthList.map(({ year, month, monthNum }, index) => {
                    return (
                      <SelectOption
                        key={`${year}-${monthNum}`}
                        value={`${year}-${monthNum}`}
                        label={`${year} ${intl.formatMessage({
                          id: `cs_perfportal_time_month_${month.toLowerCase()}`,
                          defaultMessage: `${month}`,
                        })}`}
                        data-csa-c-type='select'
                        data-csa-c-content-id='metrics-bottom-quartile-month-select'
                        data-csa-c-slotid='metrics-slot'
                        data-csa-c-tag1='Metrics'
                        data-csa-c-tag2={`metrics/${userID}`}
                        data-csa-c-tag3={`${year}-${monthNum}`}
                        data-csa-c-profile-id={requesterUserID}
                        data-csa-c-username={requesterUsername}
                        data-csa-c-is-tm={isTm}
                      />
                    );
                  })}
                </Select>
                <Tooltip
                  position='bottom'
                  title={intl.formatMessage({
                    id: 'cs_perfportal_bq_description_tooltip',
                    defaultMessage:
                      'Data becomes available at the end of each month (e.g., data for October will be available November 1).',
                  })}
                  id='bottom_quartile_trend_tooltip'
                >
                  <Text alignment='center'>
                    <Icon tokens={infoTokens} />
                  </Text>
                </Tooltip>
              </Row>
            </Row>
          </Box>
          {associateInsightsState === DataState.LOADING ||
          teamDetailsMetricsState === DataState.LOADING ? (
            <Row alignmentHorizontal='center'>
              <Loader />
            </Row>
          ) : displayedBottomQuartileData.length === 0 ? (
            <NoDataBadge />
          ) : (
            <Column
              width='100%'
              alignmentHorizontal='end'
              spacing='300'
              spacingInset='300'
            >
              <Table
                headerRows={1}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={onSort}
                showDividers={true}
                spacing='small'
              >
                <span id='nav-bottom-quartile' />
                <TableRow>
                  <TableCell
                    sortColumn={BottomQuartileTableColumns.Name}
                    alignmentVertical='bottom'
                  >
                    <FormattedMessage
                      id='cs_perfportal_bq_associate'
                      defaultMessage='Associate'
                    />
                  </TableCell>
                  <TableCell
                    sortColumn={BottomQuartileTableColumns.Metric_Name}
                    key={'metric'}
                    alignmentVertical='bottom'
                    alignmentHorizontal='end'
                  >
                    <FormattedMessage
                      id='cs_perfportal_bq_metric_type'
                      defaultMessage='Metric Type'
                    />
                  </TableCell>
                  <TableCell
                    sortColumn={BottomQuartileTableColumns.Metric_Value}
                    key={'metric'}
                    alignmentVertical='bottom'
                    alignmentHorizontal='end'
                  >
                    <FormattedMessage
                      id='cs_perfportal_bq_metric_value'
                      defaultMessage='Metric Value'
                    />
                  </TableCell>
                  <TableCell
                    sortColumn={BottomQuartileTableColumns.Benchmark}
                    key={'benchmark'}
                    alignmentVertical='bottom'
                    alignmentHorizontal='end'
                  >
                    <FormattedMessage
                      id='cs_perfportal_bq_benchmark_difference'
                      defaultMessage={'Benchmark Difference'}
                    />
                  </TableCell>
                </TableRow>

                {displayedBottomQuartileData
                  .slice(firstVisibleIndex, lastVisibleIndex)
                  .map(bottomQuartileData => (
                    <TableRow key={userId} data-cy='table-row-content'>
                      <TableCell>
                        <Text type='b300' data-testid={userId}>
                          {bottomQuartileData.userFullName}
                        </Text>
                        <Text type='b200' color='secondary'>
                          <FormattedMessage
                            id='cs_perfportal_network_benchmark'
                            defaultMessage='Network Benchmark'
                          />
                        </Text>
                      </TableCell>
                      <TableCell
                        key={'metric_name'}
                        width={computeColumnWidthAsPercent(4)}
                        alignmentHorizontal='end'
                      >
                        <FormattedMessage
                          {...getMetricStringIdAndDefaultMessage(
                            bottomQuartileData.metricName || ''
                          )}
                        />
                      </TableCell>
                      <TableCell
                        key={'metric_value'}
                        width={computeColumnWidthAsPercent(4)}
                        alignmentHorizontal='end'
                      >
                        <Text type='b300'>
                          {bottomQuartileData.metricValue
                            ? formatMetric(bottomQuartileData.metricValue)
                            : '-'}
                        </Text>
                        <Text type='b200' color='secondary'>
                          {bottomQuartileData.networkAvg
                            ? formatMetric(bottomQuartileData.networkAvg)
                            : '-'}
                        </Text>
                      </TableCell>
                      <TableCell
                        key={'metric'}
                        width={computeColumnWidthAsPercent(4)}
                        alignmentHorizontal='end'
                      >
                        <Text type='b300'>
                          {bottomQuartileData.benchmarkDiff
                            ? formatMetric(bottomQuartileData.benchmarkDiff)
                            : '-'}
                        </Text>
                      </TableCell>
                    </TableRow>
                  ))}
              </Table>
              <Pagination
                showSkipArrows={true}
                numberOfPages={totalPages}
                onChange={setCurrentPage}
                currentPage={currentPage}
              />
            </Column>
          )}
        </>
        <Divider spacingBefore='500' spacingAfter='400' />
      </>
    </>
  );
};

export default BottomQuartile;
